// src/pages/Inventory.jsx
import React, { useEffect, useMemo, useState } from "react";

// your GAS endpoint
const API_URL =
  "https://script.google.com/macros/s/AKfycbzqqTCK0qPzvNSrWejALhljNNBgQ-_WlD4zAAaXQbcwMJG84y_tgzHmAMHuWK_Qteic/exec";

// your Shows endpoint
const SHOWS_URL =
  "https://script.google.com/macros/s/AKfycbw-LKx4-dCTaoSIaW0U8vbP2R8m8QCEgZpvvGS1JgVLUHWvwRzKJj-c7s6hCRd_NpA/exec";

// special token for full-inventory mode
const ALL = "__ALL__";

// mobile-first styles injected by component
const MOBILE_STYLES = `
/* Remove any horizontal scroll on our grouped areas */
.inv-table-scroll { overflow-x: hidden !important; }

/* Accordion shell */
.inv-accordion { border-bottom: 1px solid rgba(148, 163, 184, 0.2); }
.inv-accordion-summary {
  list-style: none; cursor: pointer; padding: 10px 12px;
  background: rgba(15, 23, 42, 0.35);
}
.inv-accordion-summary::-webkit-details-marker { display: none; }
.inv-acc-title {
  display: flex; align-items: center; gap: 12px;
  justify-content: space-between; flex-wrap: wrap;
}
.inv-acc-name { font-weight: 700; font-size: 1rem; flex: 1 1 auto; }
.inv-acc-meta { display: flex; flex-direction: column; align-items: flex-end; min-width: 60px; }
.inv-acc-meta-label { font-size: 0.65rem; opacity: 0.6; text-transform: uppercase; letter-spacing: 0.02em; }
.inv-acc-meta-value { font-weight: 700; font-size: 0.95rem; color: #9ecfff; }

/* Body */
.inv-accordion-body { padding: 8px 12px 12px; display: grid; gap: 10px; }

/* Variant rows (each type/location) */
.inv-variant-row {
  display: grid; gap: 10px; grid-template-columns: 1fr;
  background: rgba(15, 23, 42, 0.25);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px; padding: 10px;
}
.inv-variant-main { display: grid; gap: 4px; }
.inv-variant-line { font-size: 0.9rem; }
.inv-variant-label { opacity: 0.7; }
.inv-variant-value { font-weight: 600; }

/* Numbers + action */
.inv-variant-stats { display: flex; gap: 14px; }
.stat { min-width: 64px; }
.stat-label { font-size: 0.7rem; opacity: 0.6; text-transform: uppercase; }
.stat-value { font-weight: 700; font-size: 0.95rem; }

/* Button area */
.inv-variant-actions { display: flex; justify-content: flex-start; }

/* Wider screens: lay out as 3 columns */
@media (min-width: 680px) {
  .inv-variant-row { grid-template-columns: 1.2fr auto auto; align-items: center; }
  .inv-variant-actions { justify-content: flex-end; }
}

/* Make "Currently in use" table mobile-friendly (stacked) */
@media (max-width: 768px) {
  .inuse-table { width: 100%; min-width: unset; border-collapse: collapse; }
  .inuse-table thead { display: none; }
  .inuse-table tbody tr {
    display: flex; flex-direction: column;
    background: rgba(15,23,42,0.3);
    margin-bottom: 10px; border-radius: 10px; padding: 10px;
  }
  .inuse-table td { display: flex; justify-content: space-between; border: none; padding: 6px 0; }
  .inuse-table td::before { content: attr(data-label); font-weight: 600; opacity: 0.7; }
  .inv-scroll-hint { display: none; }
}
`;

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [inUse, setInUse] = useState([]);
  const [shows, setShows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [activeCategory, setActiveCategory] = useState(null); // can be null, category name, or ALL
  const [modalItem, setModalItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // filter inside a category (or across ALL)
  const [locationFilter, setLocationFilter] = useState("");

  // simple sorting key retained for future use (not used in accordion mode but kept for parity)
  const [sortConfig, setSortConfig] = useState({ key: "item", dir: "asc" });

  // ----------------------------------------------------
  // load all (with fallback to old actions)
  // ----------------------------------------------------
  async function loadAll() {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API_URL}?action=all&cb=${Date.now()}`, {
        cache: "no-store",
      });
      const json = await res.json();

      // fallback if script doesn't have ?action=all
      if (!json.ok && /Unknown action/i.test(json.error || "")) {
        const [invRes, inuseRes] = await Promise.all([
          fetch(`${API_URL}?action=getInventory&cb=${Date.now()}`, {
            cache: "no-store",
          }),
          fetch(`${API_URL}?action=getCheckedOut&cb=${Date.now()}`, {
            cache: "no-store",
          }),
        ]);

        const invJson = await invRes.json();
        const inuseJson = await inuseRes.json();
        if (!invJson.ok) throw new Error(invJson.error || "Failed inventory");
        if (!inuseJson.ok) throw new Error(inuseJson.error || "Failed in use");

        setInventory(invJson.data || []);
        setInUse(inuseJson.data || []);
      } else {
        const inv = json.inventory || (json.data && json.data.inventory) || [];
        const inuse = json.inuse || (json.data && json.data.inuse) || [];
        setInventory(inv);
        setInUse(inuse);
      }
    } catch (e) {
      console.error(e);
      setErr(String(e.message || e));
      setInventory([]);
      setInUse([]);
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------------------------------
  // load shows (for dropdown)
  // ----------------------------------------------------
  async function loadShows() {
    try {
      const res = await fetch(`${SHOWS_URL}?cb=${Date.now()}`, {
        cache: "no-store",
      });
      const data = await res.json();

      let arr = [];
      if (Array.isArray(data.shows)) arr = data.shows;
      else if (Array.isArray(data.data)) arr = data.data;
      else if (Array.isArray(data.items)) arr = data.items;
      else if (Array.isArray(data.result)) arr = data.result;
      else if (Array.isArray(data)) arr = data;

      const norm = (arr || []).map((s, i) => {
        if (Array.isArray(s)) {
          return {
            id: `SHOW-${i + 1}`,
            title: s[1] || `Show ${i + 1}`,
            venue: s[2] || "",
            dates: s[0] || "",
          };
        }
        return {
          id: s.id || `SHOW-${i + 1}`,
          title:
            s.showName ||
            s["Show Name"] ||
            s.Title ||
            s.Event ||
            s.name ||
            `Show ${i + 1}`,
          venue: s.venue || s.Venue || "",
          dates: s.dates || s.Dates || "",
        };
      });

      setShows(norm);
    } catch (e) {
      console.warn("loadShows failed:", e.message);
      setShows([]);
    }
  }

  useEffect(() => {
    loadAll();
    loadShows();
  }, []);

  // ----------------------------------------------------
  // build in-use lookup: Category||Item||Type → qty
  // ----------------------------------------------------
  const inUseMap = useMemo(() => {
    const m = {};
    (inUse || []).forEach((r) => {
      const key = [
        (r.category || "").trim(),
        (r.item || "").trim(),
        (r.type || "").trim(),
      ].join("||");
      m[key] = (m[key] || 0) + Number(r.qty || 0);
    });
    return m;
  }, [inUse]);

  // ----------------------------------------------------
  // categories
  // ----------------------------------------------------
  const categories = useMemo(() => {
    const map = {};
    (inventory || []).forEach((row) => {
      const cat =
        row.category && row.category.trim()
          ? row.category.trim()
          : "Uncategorized";
      if (!map[cat]) {
        map[cat] = { name: cat, totalItems: 0, totalAvailable: 0 };
      }
      map[cat].totalItems += 1;
      map[cat].totalAvailable += Number(row.available || 0);
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [inventory]);

  // items for selected category (or ALL)
  const currentItems = useMemo(() => {
    if (!activeCategory) return [];
    if (activeCategory === ALL) return inventory || [];
    return (inventory || []).filter((row) => {
      const cat =
        row.category && row.category.trim()
          ? row.category.trim()
          : "Uncategorized";
      return cat === activeCategory;
    });
  }, [inventory, activeCategory]);

  // distinct locations for current context (category or ALL)
  const currentLocations = useMemo(() => {
    const s = new Set();
    const src =
      activeCategory === ALL || activeCategory ? currentItems : inventory;
    (src || []).forEach((it) => {
      const loc = (it.location || "").trim();
      if (loc) s.add(loc);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [currentItems, inventory, activeCategory]);

  // apply location filter
  const filteredItems = useMemo(() => {
    const src = currentItems || [];
    if (!locationFilter) return src;
    return src.filter((it) => (it.location || "").trim() === locationFilter);
  }, [currentItems, locationFilter]);

  // ----------------------------------------------------
  // helpers: compute rows and group by item
  // ----------------------------------------------------
  function computeRows(items, inUseMap) {
    return (items || []).map((it) => {
      const key = [
        (it.category || "").trim(),
        (it.item || "").trim(),
        (it.type || "").trim(),
      ].join("||");

      const total = Number(it.totalQty ?? 0);
      const outFromSheet = Number(it.checkedOut ?? 0);
      const outFromInUse = Number(inUseMap[key] || 0);
      const out = Math.max(outFromInUse, outFromSheet);
      const available =
        total > 0 ? Math.max(0, total - out) : Number(it.available ?? 0);

      return { ...it, _total: total, _out: out, _available: available };
    });
  }

  function groupByItem(rows) {
    const groups = rows.reduce((acc, r) => {
      const k = (r.item || "Unknown").trim().toLowerCase();
      if (!acc[k]) {
        acc[k] = {
          itemName: r.item || "Unknown",
          rows: [],
          sumTotal: 0,
          sumOut: 0,
          sumAvailable: 0,
        };
      }
      acc[k].rows.push(r);
      acc[k].sumTotal += r._total || 0;
      acc[k].sumOut += r._out || 0;
      acc[k].sumAvailable += r._available || 0;
      return acc;
    }, {});
    return Object.values(groups).sort((a, b) =>
      a.itemName.localeCompare(b.itemName)
    );
  }

  // ----------------------------------------------------
  // checkout submit
  // ----------------------------------------------------
  async function handleCheckoutSubmit(formData) {
    setSaving(true);
    try {
      const qs = new URLSearchParams({
        action: "checkout",
        item: formData.item || "",
        category: formData.category || "",
        type: formData.type || "",
        qty: String(formData.qty || 0),
        eventTitle: formData.eventTitle || "",
        event: formData.eventTitle || "",
        location: formData.location || "",
        name: formData.name || "",
        cb: String(Date.now()),
      }).toString();

      const res = await fetch(`${API_URL}?${qs}`, { cache: "no-store" });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Checkout failed");

      const inv = json.inventory || (json.data && json.data.inventory) || [];
      const inuse = json.inuse || (json.data && json.data.inuse) || [];

      setInventory(inv);
      setInUse(inuse);
      setModalItem(null);
    } catch (e) {
      alert("Checkout failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ----------------------------------------------------
  // return / checkin
  // ----------------------------------------------------
  async function handleReturn(checkoutId, qty) {
    try {
      const qs = new URLSearchParams({
        action: "checkin",
        id: checkoutId,
        qty: String(qty || 0),
        cb: String(Date.now()),
      }).toString();

      const res = await fetch(`${API_URL}?${qs}`, { cache: "no-store" });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "Checkin failed");

      const inv = json.inventory || (json.data && json.data.inventory) || [];
      const inuse = json.inuse || (json.data && json.data.inuse) || [];

      setInventory(inv);
      setInUse(inuse);
    } catch (e) {
      alert("Return failed: " + e.message);
    }
  }

  return (
    <div className="page inventory">
      {/* Inject mobile-first styles */}
      <style>{MOBILE_STYLES}</style>

      {/* header */}
      <div
        className="flex items-center justify-between gap-4"
        style={{ marginBottom: "0.75rem" }}
      >
        <div>
          <h1 className="text-2xl font-bold">Inventory</h1>
          <p className="text-sm" style={{ color: "rgba(226,232,240,.45)" }}>
            {activeCategory
              ? activeCategory === ALL
                ? "All categories, grouped by category → item."
                : "Items in this category."
              : "Select a category, view full inventory, or see currently-out items below."}
          </p>
        </div>
        {activeCategory && (
          <button
            onClick={() => {
              setActiveCategory(null);
              setLocationFilter("");
            }}
            className="btn dark"
          >
            ← All categories
          </button>
        )}
      </div>

      {err && <p className="error">{err}</p>}
      {loading && <p className="muted">Loading…</p>}

      {/* IN USE (mobile-stacked) */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div className="flex justify-between items-center">
          <div className="font-semibold">Currently in use</div>
          <div className="text-sm">{inUse.length} record(s)</div>
        </div>
        {inUse.length === 0 ? (
          <p className="muted" style={{ marginTop: ".5rem" }}>
            Nothing is currently checked out.
          </p>
        ) : (
          <>
            <div className="inv-table-scroll">
              <table
                className="w-full text-left inuse-table"
                style={{ marginTop: ".5rem", minWidth: "0" }}
              >
                <thead>
                  <tr>
                    <th className="p-1">Item</th>
                    <th className="p-1">Category</th>
                    <th className="p-1">Qty</th>
                    <th className="p-1">Event</th>
                    <th className="p-1">Location</th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {inUse.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="p-1" data-label="Item">
                        {r.item}
                      </td>
                      <td className="p-1" data-label="Category">
                        {r.category}
                      </td>
                      <td
                        className="p-1"
                        data-label="Qty"
                        style={{ color: "#fca5a5" }}
                      >
                        {r.qty}
                      </td>
                      <td className="p-1" data-label="Event">
                        {r.eventTitle}
                      </td>
                      <td className="p-1" data-label="Location">
                        {r.location}
                      </td>
                      <td className="p-1" data-label="Actions">
                        <button
                          onClick={() => handleReturn(r.checkoutId, r.qty)}
                          className="btn ghost"
                          style={{ fontSize: "0.7rem", padding: "3px 9px" }}
                        >
                          Return
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* CATEGORY LIST + Full inventory button */}
      {!loading && !activeCategory && (
        <>
          <div
            className="home-actions"
            style={{
              display: "grid",
              gap: "14px",
              maxWidth: "280px",
              margin: "0 auto",
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat.name}
                className="btn"
                onClick={() => {
                  setActiveCategory(cat.name);
                  setLocationFilter("");
                  setSortConfig({ key: "item", dir: "asc" });
                }}
                style={{ justifyContent: "space-between" }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{cat.name}</div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.65 }}>
                    {cat.totalItems} item{cat.totalItems === 1 ? "" : "s"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      textTransform: "uppercase",
                      opacity: 0.6,
                    }}
                  >
                    Available
                  </div>
                  <div style={{ color: "#9ecfff", fontWeight: 600 }}>
                    {cat.totalAvailable}
                  </div>
                </div>
              </button>
            ))}
            {!categories.length && (
              <div className="muted">No categories found.</div>
            )}
          </div>

          <div style={{ marginTop: "1rem", textAlign: "center" }}>
            <button
              className="btn primary"
              onClick={() => {
                setActiveCategory(ALL);
                setLocationFilter("");
                setSortConfig({ key: "item", dir: "asc" });
              }}
            >
              View full inventory
            </button>
          </div>
        </>
      )}

      {/* ITEMS FOR SELECTED CATEGORY (accordion, grouped by item) */}
      {!loading && activeCategory && activeCategory !== ALL && (
        <div className="space-y-2" style={{ marginTop: "1.25rem" }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {activeCategory}
              <span
                style={{ color: "rgba(226,232,240,.4)", fontSize: "0.8rem" }}
              >
                ({filteredItems.length} item
                {filteredItems.length === 1 ? "" : "s"})
              </span>
            </h2>

            {currentLocations.length > 0 && (
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                style={{
                  background: "rgba(15,23,42,0.45)",
                  border: "1px solid rgba(148, 163, 184, 0.4)",
                  borderRadius: "10px",
                  padding: "6px 10px",
                  color: "white",
                  fontSize: "0.8rem",
                }}
              >
                <option value="">All locations</option>
                {currentLocations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="grouped-list">
              {(() => {
                const rows = computeRows(filteredItems, inUseMap);
                const itemGroups = groupByItem(rows);

                if (!itemGroups.length) {
                  return (
                    <div className="p-3">
                      <span className="muted">No items match this filter.</span>
                    </div>
                  );
                }

                return itemGroups.map((g, i) => (
                  <details key={i} className="inv-accordion">
                    <summary className="inv-accordion-summary">
                      <div className="inv-acc-title">
                        <div className="inv-acc-name">{g.itemName}</div>
                        <div className="inv-acc-meta">
                          <div className="inv-acc-meta-label">Total</div>
                          <div className="inv-acc-meta-value">{g.sumTotal}</div>
                        </div>
                        <div className="inv-acc-meta">
                          <div className="inv-acc-meta-label">Out</div>
                          <div className="inv-acc-meta-value">{g.sumOut}</div>
                        </div>
                        <div className="inv-acc-meta">
                          <div className="inv-acc-meta-label">Avail</div>
                          <div
                            className="inv-acc-meta-value"
                            style={{
                              color:
                                g.sumAvailable === 0 ? "#fca5a5" : "inherit",
                            }}
                          >
                            {g.sumAvailable}
                          </div>
                        </div>
                      </div>
                    </summary>

                    <div className="inv-accordion-body">
                      {g.rows
                        .sort((a, b) => {
                          const at = (a.type || "").toLowerCase();
                          const bt = (b.type || "").toLowerCase();
                          if (at !== bt) return at < bt ? -1 : 1;
                          const al = (a.location || "").toLowerCase();
                          const bl = (b.location || "").toLowerCase();
                          if (al !== bl) return al < bl ? -1 : 1;
                          return 0;
                        })
                        .map((it, idx) => (
                          <div key={idx} className="inv-variant-row">
                            <div className="inv-variant-main">
                              <div className="inv-variant-line">
                                <span className="inv-variant-label">Type:</span>{" "}
                                <span className="inv-variant-value">
                                  {it.type || "—"}
                                </span>
                              </div>
                              <div className="inv-variant-line">
                                <span className="inv-variant-label">
                                  Location:
                                </span>{" "}
                                <span className="inv-variant-value">
                                  {it.location || "—"}
                                </span>
                              </div>
                            </div>

                            <div className="inv-variant-stats">
                              <div className="stat">
                                <div className="stat-label">Total</div>
                                <div className="stat-value">{it._total}</div>
                              </div>
                              <div className="stat">
                                <div className="stat-label">Out</div>
                                <div className="stat-value">{it._out}</div>
                              </div>
                              <div className="stat">
                                <div className="stat-label">Avail</div>
                                <div
                                  className="stat-value"
                                  style={{
                                    color:
                                      it._available === 0
                                        ? "#fca5a5"
                                        : "rgba(226,232,240,.95)",
                                  }}
                                >
                                  {it._available}
                                </div>
                              </div>
                            </div>

                            <div className="inv-variant-actions">
                              <button
                                disabled={it._available === 0}
                                onClick={() =>
                                  setModalItem({
                                    ...it,
                                    available: it._available,
                                    totalQty: it._total,
                                  })
                                }
                                className={
                                  it._available === 0
                                    ? "btn ghost"
                                    : "btn primary"
                                }
                                style={{
                                  fontSize: "0.8rem",
                                  padding: "6px 12px",
                                }}
                              >
                                Check out
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </details>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* FULL INVENTORY (ALL categories → category accordion → item accordion) */}
      {!loading && activeCategory === ALL && (
        <div className="space-y-2" style={{ marginTop: "1.25rem" }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Full inventory</h2>
            {currentLocations.length > 0 && (
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                style={{
                  background: "rgba(15,23,42,0.45)",
                  border: "1px solid rgba(148, 163, 184, 0.4)",
                  borderRadius: "10px",
                  padding: "6px 10px",
                  color: "white",
                  fontSize: "0.8rem",
                }}
              >
                <option value="">All locations</option>
                {currentLocations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="card" style={{ padding: 0 }}>
            {categories.map((cat, ci) => {
              // items for this category (respect locationFilter if set)
              const catItems = (inventory || []).filter((row) => {
                const c =
                  row.category && row.category.trim()
                    ? row.category.trim()
                    : "Uncategorized";
                if (c !== cat.name) return false;
                if (!locationFilter) return true;
                return (row.location || "").trim() === locationFilter;
              });

              const rows = computeRows(catItems, inUseMap);
              if (!rows.length) return null;

              const itemGroups = groupByItem(rows);
              const sumTotal = rows.reduce((a, r) => a + (r._total || 0), 0);
              const sumOut = rows.reduce((a, r) => a + (r._out || 0), 0);
              const sumAvail = rows.reduce(
                (a, r) => a + (r._available || 0),
                0
              );

              return (
                <details key={ci} className="inv-accordion">
                  <summary className="inv-accordion-summary">
                    <div className="inv-acc-title">
                      <div className="inv-acc-name">{cat.name}</div>
                      <div className="inv-acc-meta">
                        <div className="inv-acc-meta-label">Total</div>
                        <div className="inv-acc-meta-value">{sumTotal}</div>
                      </div>
                      <div className="inv-acc-meta">
                        <div className="inv-acc-meta-label">Out</div>
                        <div className="inv-acc-meta-value">{sumOut}</div>
                      </div>
                      <div className="inv-acc-meta">
                        <div className="inv-acc-meta-label">Avail</div>
                        <div
                          className="inv-acc-meta-value"
                          style={{
                            color: sumAvail === 0 ? "#fca5a5" : "inherit",
                          }}
                        >
                          {sumAvail}
                        </div>
                      </div>
                    </div>
                  </summary>

                  <div className="inv-accordion-body">
                    {itemGroups.map((g, gi) => (
                      <details key={gi} className="inv-accordion">
                        <summary className="inv-accordion-summary">
                          <div className="inv-acc-title">
                            <div className="inv-acc-name">{g.itemName}</div>
                            <div className="inv-acc-meta">
                              <div className="inv-acc-meta-label">Total</div>
                              <div className="inv-acc-meta-value">
                                {g.sumTotal}
                              </div>
                            </div>
                            <div className="inv-acc-meta">
                              <div className="inv-acc-meta-label">Out</div>
                              <div className="inv-acc-meta-value">
                                {g.sumOut}
                              </div>
                            </div>
                            <div className="inv-acc-meta">
                              <div className="inv-acc-meta-label">Avail</div>
                              <div
                                className="inv-acc-meta-value"
                                style={{
                                  color:
                                    g.sumAvailable === 0
                                      ? "#fca5a5"
                                      : "inherit",
                                }}
                              >
                                {g.sumAvailable}
                              </div>
                            </div>
                          </div>
                        </summary>

                        <div className="inv-accordion-body">
                          {g.rows
                            .sort((a, b) => {
                              const at = (a.type || "").toLowerCase();
                              const bt = (b.type || "").toLowerCase();
                              if (at !== bt) return at < bt ? -1 : 1;
                              const al = (a.location || "").toLowerCase();
                              const bl = (b.location || "").toLowerCase();
                              if (al !== bl) return al < bl ? -1 : 1;
                              return 0;
                            })
                            .map((it, idx) => (
                              <div key={idx} className="inv-variant-row">
                                <div className="inv-variant-main">
                                  <div className="inv-variant-line">
                                    <span className="inv-variant-label">
                                      Type:
                                    </span>{" "}
                                    <span className="inv-variant-value">
                                      {it.type || "—"}
                                    </span>
                                  </div>
                                  <div className="inv-variant-line">
                                    <span className="inv-variant-label">
                                      Location:
                                    </span>{" "}
                                    <span className="inv-variant-value">
                                      {it.location || "—"}
                                    </span>
                                  </div>
                                </div>

                                <div className="inv-variant-stats">
                                  <div className="stat">
                                    <div className="stat-label">Total</div>
                                    <div className="stat-value">
                                      {it._total}
                                    </div>
                                  </div>
                                  <div className="stat">
                                    <div className="stat-label">Out</div>
                                    <div className="stat-value">{it._out}</div>
                                  </div>
                                  <div className="stat">
                                    <div className="stat-label">Avail</div>
                                    <div
                                      className="stat-value"
                                      style={{
                                        color:
                                          it._available === 0
                                            ? "#fca5a5"
                                            : "rgba(226,232,240,.95)",
                                      }}
                                    >
                                      {it._available}
                                    </div>
                                  </div>
                                </div>

                                <div className="inv-variant-actions">
                                  <button
                                    disabled={it._available === 0}
                                    onClick={() =>
                                      setModalItem({
                                        ...it,
                                        available: it._available,
                                        totalQty: it._total,
                                      })
                                    }
                                    className={
                                      it._available === 0
                                        ? "btn ghost"
                                        : "btn primary"
                                    }
                                    style={{
                                      fontSize: "0.8rem",
                                      padding: "6px 12px",
                                    }}
                                  >
                                    Check out
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </details>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}

      {/* CHECKOUT MODAL */}
      {modalItem && (
        <CheckoutModal
          item={modalItem}
          onClose={() => setModalItem(null)}
          onSubmit={handleCheckoutSubmit}
          saving={saving}
          shows={shows}
        />
      )}
    </div>
  );
}

/* =========================================================
   Checkout modal (unchanged, just styled for mobile)
   ========================================================= */
function CheckoutModal({ item, onClose, onSubmit, saving, shows = [] }) {
  const [qty, setQty] = useState(1);
  const [eventTitle, setEventTitle] = useState("");
  const [location, setLocation] = useState(item.location || "");
  const [name, setName] = useState("");

  const max = item.available ?? 0;

  function handleShowPick(e) {
    const val = e.target.value;
    if (!val) return;
    const chosen = shows.find((s) => String(s.id) === String(val));
    if (!chosen) return;
    if (!eventTitle) setEventTitle(chosen.title || "");
    if (!location) setLocation(chosen.venue || "");
  }

  function submit() {
    if (qty < 1) return;
    onSubmit({
      category: item.category,
      item: item.item,
      type: item.type,
      qty: Number(qty),
      eventTitle,
      location,
      name,
    });
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div className="modal-title">
            Check out: {item.item} {item.type ? `(${item.type})` : ""}
          </div>
          <button onClick={onClose} className="modal-close">
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="muted" style={{ marginBottom: "0.5rem" }}>
            Available: {item.available} / Total: {item.totalQty}
          </p>

          <div className="field">
            <label>Quantity</label>
            <input
              type="number"
              min="1"
              max={max || undefined}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Assign to show (optional)</label>
            <select
              defaultValue=""
              onChange={handleShowPick}
              style={{
                background: "rgba(15,23,42,0.45)",
                border: "1px solid rgba(148, 163, 184, 0.4)",
                borderRadius: "10px",
                padding: "8px 10px",
                color: "white",
                fontSize: "0.85rem",
              }}
            >
              <option value="">— Select show —</option>
              {shows.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title} {s.venue ? `(${s.venue})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Event Title</label>
            <input
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              placeholder="Tech run, Opening, ..."
            />
          </div>

          <div className="field">
            <label>Location</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Red Theatre / Black Box / Offsite"
            />
          </div>

          <div className="field">
            <label>Name (responsible)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Person taking it"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="btn ghost">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} className="btn primary">
            {saving ? "Saving…" : "Check out"}
          </button>
        </div>
      </div>
    </div>
  );
}
